// @flow

import React from 'react';
import styled from 'styled-components';
import type { StatusIndicatorThemeType } from './theme';
import { StatusIndicatorTheme } from './theme';

type Props = {
  /**
   * Text that will appear to the right of the indicator dot.
   */
  children?: string,
  /**
   * When true, applies a light color to text; set to false by default.
   */
  lightText?: boolean,
};

const StatusIndicator = (props: {
  ...Props,
  theme: StatusIndicatorThemeType,
}) => {
  const { lightText, children, theme } = props;
  const textColor = lightText ? theme.lightTextColor : theme.darkTextColor;

  return (
    <CenteredSpan>
      <IndicatorDot dotColor={theme.dotColor} />
      <IndicatorText textColor={textColor}>{children}</IndicatorText>
    </CenteredSpan>
  );
};

const IndicatorDot = styled.span`
  ${({ dotColor }) => {
    return `background-color: ${dotColor};`;
  }}

  margin-right: 8px;
  width: 8px;
  height: 8px;
  top: 6px;
  border-radius: 50%;
  display: inline-block;
  vertical-align: middle;
`;

const IndicatorText = styled.span`
  ${({ textColor }) => {
    return `color: ${textColor};`;
  }}

  display: inline-block;
  vertical-align: middle;
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 0.875rem;
  line-height: 1.25rem;
  weight: 400;
`;

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const CenteredSpan = styled.span`
  display: flex;
  align-items: center;
`;

StatusIndicator.defaultProps = {
  lightText: false,
};

const OpenStatusIndicator = (props: Props) => {
  return <StatusIndicator {...props} theme={StatusIndicatorTheme.OPEN} />;
};
OpenStatusIndicator.displayName = 'OpenStatusIndicator';

const ClosedStatusIndicator = (props: Props) => {
  return <StatusIndicator {...props} theme={StatusIndicatorTheme.CLOSED} />;
};
ClosedStatusIndicator.displayName = 'ClosedStatusIndicator';

const DraftStatusIndicator = (props: Props) => {
  return <StatusIndicator {...props} theme={StatusIndicatorTheme.DRAFT} />;
};
DraftStatusIndicator.displayName = 'DraftStatusIndicator';

const CompletedStatusIndicator = (props: Props) => {
  return <StatusIndicator {...props} theme={StatusIndicatorTheme.COMPLETED} />;
};
CompletedStatusIndicator.displayName = 'CompletedStatusIndicator';

const OffStatusIndicator = (props: Props) => {
  return <StatusIndicator {...props} theme={StatusIndicatorTheme.OFF} />;
};
OffStatusIndicator.displayName = 'OffStatusIndicator';

const DisabledStatusIndicator = (props: Props) => {
  return <StatusIndicator {...props} theme={StatusIndicatorTheme.DISABLED} />;
};
DisabledStatusIndicator.displayName = 'DisabledStatusIndicator';

export {
  OpenStatusIndicator,
  ClosedStatusIndicator,
  DraftStatusIndicator,
  CompletedStatusIndicator,
  OffStatusIndicator,
  DisabledStatusIndicator,
};
